import { Task } from 'views/pages/MyTasks/TaskDetail/types'
import { useTranslation } from 'react-i18next'
import { Icons, Progress, TextArea, Text } from '@agro-club/agroclub-shared'
import { AButton, ARouterLink } from 'views/components/Analytics'
import { generatePath } from 'react-router'
import { refetchFunc } from 'modules/domain/common/hooks'
import { Card } from 'views/components/Card/Card'
import { InfoBlock, InTaskBtnsContainer } from '../../styled'
import { endpoints } from 'modules/endpoints'
import { useAProgress } from 'hooks/useAProgress'
import { apiClient } from 'modules/utils/httpClient'
import { useNotification } from 'hooks/useNotification'
import { MultilineText } from 'views/styled/common'
import styled from 'styled-components'
import { useState, useMemo } from 'react'
import CarriersKRRoutes from 'views/pages/LogisticsKR/CarriersKR/routes'
import { Hr } from 'views/components/Hr/Hr'
import { DriverDocuments } from 'views/pages/LogisticsKR/CarriersKR/CarrierKRDetails/CarrierAccordion/DriversDocuments/DriverDocuments'
import { TaskStatus } from 'modules/domain/task/types'
import { SetTaskToWorkButton } from '../StatusButtons'

const ButtonsWrapper = styled.div`
  display: flex;
  align-items: center;
  column-gap: 8px;
  margin-top: 16px;
`

interface Props {
  task: Task
  taskRefetch: refetchFunc
}

export const СarrierSecurityVerification = ({ task, taskRefetch }: Props) => {
  const { t } = useTranslation('my_tasks')
  const notify = useNotification()
  const [comment, setComment] = useState('')
  const carrierId = task.other_links?.['carrier']?.id
  const carrierVerified = task.other_links?.['carrier']?.is_verified_by_security

  const driverId = task.other_links?.['driver']?.id
  const driverVerified = task.other_links?.['driver']?.is_verified_by_security

  const agentId = task.other_links?.['agent']?.id
  const agentVerified = task.other_links?.['agent']?.is_verified_by_security

  const onSuccess = async () => {
    await taskRefetch()
  }

  const onError = (error) => {
    if (!error?.errors) return
    const errKeys = Object.keys(error.errors)
    let err = 'Ошибка'
    if (errKeys.length && error.errors[errKeys[0]]) {
      err = error.errors[errKeys[0]].toString()
    }
    notify(Progress.ERROR, { title: err })
  }

  const [progress, completeTask] = useAProgress(
    () => {
      return apiClient.post(endpoints.completeTask(task.id.toString()), { response_comment: comment })
    },
    {
      eventName: 'finishVerification',
      analyticsPayload: {
        id: task.id.toString(),
      },
      onSuccess,
      onError,
    },
  )

  const [carrierProgress, approveCarrier] = useAProgress(
    () => {
      return apiClient.post(endpoints.LogisticsKR.securityApproveCarrier(carrierId?.toString() ?? ''))
    },
    {
      eventName: 'carrierVerification',
      analyticsPayload: {
        id: task.id.toString(),
      },
      onSuccess,
      onError,
    },
  )

  const [driverProgress, approveDriver] = useAProgress(
    () => {
      return apiClient.post(endpoints.LogisticsKR.securityApproveDriver(driverId?.toString() ?? ''))
    },
    {
      eventName: 'driverVerification',
      analyticsPayload: {
        id: task.id.toString(),
      },
      onSuccess,
      onError,
    },
  )

  const [agentProgress, approveAgent] = useAProgress(
    () => {
      return apiClient.post(endpoints.LogisticsKR.securityApproveAgent(agentId?.toString() ?? ''))
    },
    {
      eventName: 'agentVerification',
      analyticsPayload: {
        id: task.id.toString(),
      },
      onSuccess,
      onError,
    },
  )
  const textWithButtons = () => {
    if (!task.comment) return null

    // Split the comment by the placeholders
    const parts = task.comment.split(
      /(<button_for_carrier_approval>)|(<button_for_driver_approval>)|(<button_for_agent_approval>)/,
    )

    return (
      <>
        {parts.map((part, index) => {
          if (part === '<button_for_carrier_approval>') {
            return (
              <>
                <InTaskBtnsContainer key={`button-carrier-${index}`}>
                  <AButton
                    id="submit|carrierApproval"
                    color="approve"
                    onClick={approveCarrier}
                    progress={carrierProgress}
                    disabled={carrierVerified || task.status !== TaskStatus.in_progress}
                  >
                    {carrierVerified ? t('approved') : t('approve')}
                  </AButton>
                </InTaskBtnsContainer>
                <Hr />
              </>
            )
          } else if (part === '<button_for_driver_approval>') {
            return (
              <>
                {driverId && (
                  <div style={{ marginTop: '16px' }}>
                    <DriverDocuments driverId={driverId} readOnly />
                  </div>
                )}
                <InTaskBtnsContainer key={`button-driver-${index}`}>
                  <AButton
                    key={`button-driver-${index}`}
                    id="submit|driverApproval"
                    color="approve"
                    onClick={approveDriver}
                    progress={driverProgress}
                    disabled={driverVerified || task.status !== TaskStatus.in_progress}
                  >
                    {driverVerified ? t('approved') : t('approve')}
                  </AButton>
                </InTaskBtnsContainer>
                <Hr />
              </>
            )
          } else if (part === '<button_for_agent_approval>') {
            return (
              <>
                <InTaskBtnsContainer key={`button-agent-${index}`}>
                  <AButton
                    key={`button-agent-${index}`}
                    id="submit|agentApproval"
                    color="approve"
                    onClick={approveAgent}
                    progress={agentProgress}
                    disabled={agentVerified || task.status !== TaskStatus.in_progress}
                  >
                    {agentVerified ? t('approved') : t('approve')}
                  </AButton>
                </InTaskBtnsContainer>
                <Hr />
              </>
            )
          }

          // Return regular text part
          return <span key={`text-${index}`}>{part}</span>
        })}
      </>
    )
  }

  const commentContent = useMemo(textWithButtons, [
    task.comment,
    task.status,
    approveCarrier,
    carrierProgress,
    carrierVerified,
    t,
    driverId,
    approveDriver,
    driverProgress,
    driverVerified,
    approveAgent,
    agentProgress,
    agentVerified,
  ])
  if (!carrierId) return null
  return (
    <Card.Container>
      <Card.GapableContent>
        <Card.Title>{t('detail.description')}</Card.Title>
        <InfoBlock>
          <Hr />
          <MultilineText>{commentContent}</MultilineText>
          <div>
            Подробнее на{' '}
            <ARouterLink
              eventName="carrierFromTask"
              itemId={carrierId.toString()}
              to={generatePath(CarriersKRRoutes.Details, { id: carrierId })}
              target="_blank"
            >
              странице перевозчика
            </ARouterLink>
          </div>
          <div style={{ marginTop: '16px' }}>
            {task.status === TaskStatus.in_progress && (
              <>
                <div style={{ marginBottom: '4px' }}>
                  <Text> {t('common:comment')}:</Text>
                </div>
                <TextArea value={comment} onChange={(e) => setComment(e.target.value)} />
                <ButtonsWrapper>
                  <AButton
                    color="secondary"
                    id="submit|aproveCarrier"
                    Icon={Icons.IconCheck}
                    onClick={completeTask}
                    progress={progress}
                  >
                    {t('executeTask')}
                  </AButton>
                </ButtonsWrapper>
              </>
            )}
            {task.status === TaskStatus.new && (
              <SetTaskToWorkButton taskId={task.id.toString()} refetch={taskRefetch} hideCurrentStatus />
            )}
          </div>
        </InfoBlock>
      </Card.GapableContent>
    </Card.Container>
  )
}
